import type { Column } from '@tanstack/react-table';
import { CheckIcon, PlusCircleIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib';
import { Badge } from '../badge';
import { Button } from '../button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../command';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Separator } from '../separator';
import { EMPTY_FILTER_VALUE, isEmptyValue } from './data-table';

export type FacetOption = { label: string; value: string };

interface DataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title: string;
  /** Explicit options; when omitted, derived from the column's unique values. */
  options?: FacetOption[];
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const { t } = useTranslation();
  const facets = column?.getFacetedUniqueValues();
  const selected = new Set((column?.getFilterValue() as string[]) ?? []);

  const resolvedOptions = useMemo(() => {
    if (options) {
      return options.map(option => ({ ...option, count: facets?.get(option.value) }));
    }
    // Group case-only variants (Renault / RENAULT) into one option, summing their counts.
    const grouped = new Map<string, { label: string; value: string; count: number }>();
    for (const [rawValue, count] of facets ?? []) {
      if (rawValue == null || rawValue === '') {
        continue;
      }
      const value = String(rawValue);
      const key = value.toLowerCase();
      const existing = grouped.get(key);
      if (existing) {
        existing.count += count;
      } else {
        grouped.set(key, { label: value, value, count });
      }
    }
    return Array.from(grouped.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [options, facets]);

  // Empty-value rows aren't in the facet map (arrays are flattened away); count them directly.
  const facetedRows = column?.getFacetedRowModel().rows;
  const emptyCount = useMemo(
    () =>
      column
        ? (facetedRows?.reduce(
            (count, row) => (isEmptyValue(row.getValue(column.id)) ? count + 1 : count),
            0,
          ) ?? 0)
        : 0,
    [column, facetedRows],
  );
  const allOptions =
    emptyCount > 0
      ? [
          ...resolvedOptions,
          { label: t('dataTable.emptyValue'), value: EMPTY_FILTER_VALUE, count: emptyCount },
        ]
      : resolvedOptions;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed">
          <PlusCircleIcon className="mr-2 size-4" />
          {title}
          {selected.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <div className="flex gap-1">
                {selected.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {selected.size} {t('dataTable.selectedPlural')}
                  </Badge>
                ) : (
                  allOptions
                    .filter(option => selected.has(option.value))
                    .map(option => (
                      <Badge
                        key={option.value}
                        variant="secondary"
                        className="rounded-sm px-1 font-normal capitalize"
                      >
                        {option.label.toLowerCase()}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>{t('dataTable.noOptions')}</CommandEmpty>
            <CommandGroup>
              {allOptions.map((option, index) => {
                const isSelected = selected.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    // cmdk lowercases item values for its active-item identity; suffix keeps any
                    // case-only variants passed via explicit `options` from sharing one highlight.
                    value={`${option.value}-${index}`}
                    onSelect={() => {
                      const next = new Set(selected);
                      if (isSelected) {
                        next.delete(option.value);
                      } else {
                        next.add(option.value);
                      }
                      const values = Array.from(next);
                      column?.setFilterValue(values.length ? values : undefined);
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="size-3.5" />
                    </div>
                    <span className="capitalize">{option.label.toLowerCase()}</span>
                    {option.count != null && (
                      <span className="ml-auto flex size-4 items-center justify-center font-mono text-xs text-muted-foreground">
                        {option.count}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selected.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => column?.setFilterValue(undefined)}
                    className="justify-center text-center"
                  >
                    {t('dataTable.clearFilters')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
